import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Gift, Sparkles, Trophy } from "lucide-react";

interface ReferralNotification {
  id: string;
  type: string;
  referee_name: string | null;
  days_awarded: number;
}

interface ReferralNotificationModalProps {
  notification: ReferralNotification | null;
  onClose: () => void;
}

export const ReferralNotificationModal = ({ 
  notification, 
  onClose 
}: ReferralNotificationModalProps) => {
  if (!notification) return null;

  const isUpgradeBonus = notification.type === 'upgrade_bonus';
  const isLegendBonus = notification.type === 'legend_bonus';

  return (
    <Dialog open={!!notification} onOpenChange={() => onClose()}>
      <DialogContent className="bg-background border-primary/30 max-w-md text-center p-8">
        <div className="flex flex-col items-center gap-6">
          {/* Animated Icon */}
          <div className="relative">
            <div className="absolute inset-0 bg-primary/20 rounded-full blur-xl animate-pulse" />
            <div className="relative bg-gradient-to-br from-primary to-primary/60 rounded-full p-6">
              {isLegendBonus ? (
                <Trophy className="w-12 h-12 text-primary-foreground" />
              ) : (
                <Gift className="w-12 h-12 text-primary-foreground" />
              )}
            </div>
            <Sparkles className="absolute -top-2 -right-2 w-6 h-6 text-yellow-400 animate-bounce" />
          </div>

          {/* Title */}
          <h2 className="text-2xl font-bold text-foreground">
            {isLegendBonus ? "🏆 Evolver Founder Unlocked!" : "🎉 Referral Bonus Earned!"}
          </h2>

          {/* Message */}
          <div className="space-y-2">
            <p className="text-muted-foreground">
              {isUpgradeBonus && (
                <>
                  <span className="text-primary font-semibold">{notification.referee_name}</span>
                  {" "}just upgraded to Premium!
                </>
              )}
              {isLegendBonus && (
                <>
                  <span className="text-primary font-semibold">{notification.referee_name}</span>
                  {" "}hit Legend status (90 days)!
                </>
              )}
            </p>
            <p className="text-foreground font-medium">
              You've earned <span className="text-primary text-xl font-bold">+{notification.days_awarded} days</span> of free Premium!
            </p>
            {isLegendBonus && (
              <p className="text-sm text-muted-foreground mt-2">
                You've also unlocked the exclusive <span className="text-primary">Evolver Founder</span> badge!
              </p>
            )}
          </div>

          {/* CTA */}
          <Button 
            onClick={onClose}
            className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold py-6"
          >
            Awesome! Keep Evolving
          </Button>

          {/* Subtle footer */}
          <p className="text-xs text-muted-foreground">
            Share your link to earn more rewards
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
